<template>
  <div class="header-search">
    <div class="search-container" @click="activateSearch">
      <svg-icon class="search-icon" icon-class="search" />
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        placeholder="查找页面"
        class="search-input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="selectFirst"
        @keydown.down="selectNext"
        @keydown.up="selectPrev"
        @keydown.esc="handleEsc"
      />
    </div>
    <div v-if="showOptions && options.length > 0" class="search-dropdown">
      <div
        v-for="(item, index) in options"
        :key="(item.path || 'opt') + '-' + index"
        :class="['search-option', { 'is-active': index === selectedIndex }]"
        @click="selectOption(item)"
        @mouseenter="selectedIndex = index"
      >
        {{ item.title.join(' > ') }}
      </div>
    </div>
  </div>
</template>

<script>
import Fuse from 'fuse.js'
import { mapGetters } from 'vuex'

/** 按 URL 规则拼接菜单 path，避免 Node path.resolve 在 Windows 下拼出反斜杠导致跳转失败 */
function resolveMenuPath(basePath, routePath) {
  if (routePath == null || routePath === '') {
    return (basePath || '/').replace(/\\/g, '/') || '/'
  }
  const child = String(routePath).replace(/\\/g, '/')
  if (/^https?:\/\//.test(child)) return child
  if (child.charAt(0) === '/') {
    return child.replace(/\/+/g, '/') || '/'
  }
  const base = String(basePath || '/').replace(/\\/g, '/').replace(/\/+$/, '')
  const joined = (base === '/' ? '' : base) + '/' + child.replace(/^\/+/, '')
  return joined.replace(/\/+/g, '/') || '/'
}

export default {
  name: 'HeaderSearch',
  data() {
    return {
      search: '',
      options: [],
      searchPool: [],
      showOptions: false,
      selectedIndex: -1,
      fuse: undefined
    }
  },
  computed: {
    ...mapGetters(['permission_routers']),
    routes() {
      var route = this.permission_routers
      return route
    }
  },
  watch: {
    routes() {
      this.searchPool = this.generateRoutes(this.routes)
    },
    searchPool(list) {
      this.initFuse(list)
    },
    showOptions(value) {
      if (value) {
        document.body.addEventListener('click', this.handleBodyClick)
      } else {
        document.body.removeEventListener('click', this.handleBodyClick)
      }
    }
  },
  mounted() {
    this.searchPool = this.generateRoutes(this.routes)
    this.initResponsiveSearch()
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.handleBodyClick)
  },
  methods: {
    activateSearch() {
      // 激活搜索输入框
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    handleInput() {
      // 处理输入变化
      this.selectedIndex = -1
      this.querySearch(this.search)
    },
    handleFocus() {
      // 输入框获得焦点
      if (this.search && this.options.length > 0) {
        this.showOptions = true
      }
    },
    handleBlur() {
      // 输入框失去焦点 - 延迟关闭以支持选项点击
      setTimeout(() => {
        this.showOptions = false
      }, 200)
    },
    handleBodyClick(event) {
      // 点击外部关闭下拉框
      if (!this.$el.contains(event.target)) {
        this.showOptions = false
      }
    },
    selectOption(option) {
      // 选择搜索选项
      this.navigateTo(option)
    },
    selectFirst() {
      // 回车选择第一个选项或当前选中选项
      if (this.options.length > 0) {
        const selectedOption = this.selectedIndex >= 0 ? this.options[this.selectedIndex] : this.options[0]
        this.navigateTo(selectedOption)
      }
    },
    selectNext() {
      // 下箭头选择下一个
      if (this.selectedIndex < this.options.length - 1) {
        this.selectedIndex++
      }
    },
    selectPrev() {
      // 上箭头选择上一个
      if (this.selectedIndex > 0) {
        this.selectedIndex--
      }
    },
    handleEsc() {
      // ESC关闭下拉框
      this.showOptions = false
      this.$refs.searchInput.blur()
    },
    navigateTo(option) {
      if (!option || !option.path) return
      this.search = ''
      this.options = []
      this.showOptions = false
      this.$router.push({
        path: option.path,
        query: {
          t: Date.now()
        }
      })
    },
    initFuse(list) {
      this.fuse = new Fuse(list, {
        shouldSort: true,
        threshold: 0.4,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
          {
            name: 'title',
            weight: 0.7
          },
          {
            name: 'path',
            weight: 0.3
          }
        ]
      })
    },
    // Filter out the routes that can be displayed in the sidebar
    // And generate the internationalized title
    generateRoutes(routes, basePath = '/', prefixTitle = []) {
      let res = []

      for (const router of routes) {
        // skip hidden router
        if (router.hidden) {
          continue
        }

        const data = {
          path: resolveMenuPath(basePath, router.path),
          title: [...prefixTitle]
        }

        if (router.meta && router.meta.title) {
          data.title = [...data.title, router.meta.title]

          // 判断是否是页面
          if (router.redirect !== 'noredirect' && router.is_group === 'N') {
            // only push the routes with title
            // special case: need to exclude parent router without redirect
            res.push(data)
          }
        }

        // recursive child routes
        if (router.children) {
          const tempRoutes = this.generateRoutes(
            router.children,
            data.path,
            data.title
          )
          if (tempRoutes.length >= 1) {
            res = [...res, ...tempRoutes]
          }
        }
      }
      return res
    },
    querySearch(query) {
      if (!this.fuse || query === '') {
        this.options = []
        this.showOptions = false
        return
      }
      this.options = this.fuse.search(query)
      this.showOptions = this.options.length > 0
    },
    initResponsiveSearch() {
      // 响应式搜索框初始化
      const handleResize = () => {
        const searchContainer = this.$el
        if (!searchContainer) return

        const screenWidth = window.innerWidth

        // 根据屏幕尺寸调整搜索框占位符
        const placeholder = this.$refs.searchInput
        if (placeholder) {
          if (screenWidth <= 768) {
            placeholder.placeholder = '搜索'
          } else if (screenWidth <= 1200) {
            placeholder.placeholder = '查找页面'
          } else {
            placeholder.placeholder = '查找页面 / Search Pages'
          }
        }
      }

      // 初始化和监听窗口大小变化
      handleResize()
      window.addEventListener('resize', handleResize)

      // 组件销毁时清理监听器
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', handleResize)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/* 响应式断点定义 */
$mobile-breakpoint: 768px;
$tablet-breakpoint: 1200px;

/* Material Design 设计令牌 */
$primary-color: #1976d2;
$primary-light: #42a5f5;
$surface-color: #ffffff;
$on-surface: #212121;
$on-surface-variant: #757575;
$outline: #e0e0e0;
$shadow-2dp: 0 2px 4px 0 rgba(0, 0, 0, 0.12), 0 1px 2px 0 rgba(0, 0, 0, 0.24);
$shadow-4dp: 0 4px 8px 0 rgba(0, 0, 0, 0.12), 0 2px 4px 0 rgba(0, 0, 0, 0.08);

.header-search {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 300px;
  min-width: 200px;

  /* 响应式宽度调整 */
  @media (max-width: $mobile-breakpoint) {
    max-width: 180px;
    min-width: 120px;
  }

  @media (min-width: #{$mobile-breakpoint + 1px}) and (max-width: $tablet-breakpoint) {
    max-width: 250px;
    min-width: 180px;
  }

  @media (min-width: #{$tablet-breakpoint + 1px}) {
    max-width: 380px;
    min-width: 280px;
  }

  .search-container {
    display: flex;
    align-items: center;
    height: 32px;
    cursor: text;
    border: none;
    border-radius: 24px;
    padding: 0 16px;
    background: $surface-color;
    transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
    box-shadow: $shadow-2dp;

    &:hover {
      box-shadow: $shadow-4dp;
    }

    &:focus-within {
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.12), $shadow-4dp;
    }

    /* 移动端优化 */
    @media (max-width: $mobile-breakpoint) {
      height: 36px;
      padding: 0 12px;
      border-radius: 18px;
    }

    .search-icon {
      font-size: 20px;
      color: $on-surface-variant;
      margin-right: 12px;
      flex-shrink: 0;
      cursor: pointer;
      transition: color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);

      @media (max-width: $mobile-breakpoint) {
        font-size: 18px;
        margin-right: 8px;
      }
    }

    .search-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font-size: 13px;
      color: $on-surface;
      padding: 0;
      height: 24px;
      line-height: 24px;

      /* Safari 和移动端优化 */
      -webkit-appearance: none;
      -webkit-user-select: auto;
      -webkit-tap-highlight-color: transparent;

      &::placeholder {
        color: $on-surface-variant;
      }

      &:focus {
        outline: none;
      }

      /* 移动端字体调整 */
      @media (max-width: $mobile-breakpoint) {
        font-size: 16px; /* 防止iOS缩放 */
      }
    }
  }

  .search-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: $surface-color;
    border: 1px solid $outline;
    border-radius: 8px;
    box-shadow: $shadow-4dp;
    z-index: 2000;
    max-height: 240px;
    overflow-y: auto;

    /* 移动端优化 */
    @media (max-width: $mobile-breakpoint) {
      position: fixed;
      top: 60px;
      left: 8px;
      right: 8px;
      border-radius: 12px;
      max-height: 300px;
    }

    .search-option {
      padding: 12px 16px;
      cursor: pointer;
      font-size: 13px;
      color: $on-surface;
      line-height: 1.5;
      transition: background-color 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);

      &:hover,
      &.is-active {
        background-color: rgba(25, 118, 210, 0.08);
        color: $primary-color;
      }

      &:not(:last-child) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      }

      /* 移动端触摸优化 */
      @media (max-width: $mobile-breakpoint) {
        padding: 16px;
        font-size: 16px;
        min-height: 48px; /* 符合Material Design触摸目标 */
        display: flex;
        align-items: center;
      }
    }
  }
}
</style>
